/**
 * Sprint 23.6 v2 — AgentRetroSnippet (Hero section 5, conditional).
 *
 * The most recent retrospective Page for this agent (Q8): a header row with
 * "Latest retrospective · {relative time}" on the left and a "View full retro →"
 * deeplink on the right, then a single flowing card — the retro title in primary
 * 500, the body excerpt in secondary. Hides the whole section when no retros
 * exist (Q24).
 *
 * Visual contract: card = background-secondary, border-radius-md, padding
 * 8px 12px, 12px / line-height 1.5 secondary.
 */

import { useMemo, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { useAgentLearnings } from "../../hooks/use-agent-learnings";

export interface AgentRetroSnippetProps {
  agentId: string;
}

export function AgentRetroSnippet({ agentId }: AgentRetroSnippetProps) {
  const navigate = useNavigate();
  const learnings = useAgentLearnings(agentId);

  const latest = useMemo(() => {
    const arr = [...learnings.retrospectives].sort(
      (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
    );
    return arr[0] ?? null;
  }, [learnings.retrospectives]);

  if (learnings.isLoading || !latest) return null;

  const excerpt = firstLines(latest.document ?? "", 200);

  return (
    <section style={wrap}>
      <div style={headerRow}>
        <h3 style={headerTitle}>Latest retrospective · {relativeTime(latest.created_at)}</h3>
        <button type="button" style={link} onClick={() => navigate(`/pages/${latest.id}`)}>
          View full retro →
        </button>
      </div>
      <div style={card}>
        <span style={snippetTitle}>{latest.name}</span>
        {excerpt && <span> {excerpt}</span>}
      </div>
    </section>
  );
}

function firstLines(doc: string, max: number): string {
  let body = doc;
  if (body.startsWith("---")) {
    const end = body.indexOf("\n---", 3);
    if (end >= 0) body = body.slice(end + 4);
  }
  const lines = body
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith("#"));
  const text = lines.slice(0, 2).join(" ");
  if (text.length <= max) return text;
  // Trim to a word boundary so the excerpt never cuts mid-word.
  const clipped = text.slice(0, max);
  const lastSpace = clipped.lastIndexOf(" ");
  return `${(lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped).trimEnd()} …`;
}

function relativeTime(iso: string): string {
  const t = Date.parse(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(t)) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const wrap: CSSProperties = {
  padding: "12px 0 14px",
  borderBottom: "0.5px solid var(--color-border-tertiary)",
};

const headerRow: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  marginBottom: 6,
  gap: 8,
};

const headerTitle: CSSProperties = {
  margin: 0,
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const link: CSSProperties = {
  padding: 0,
  border: "none",
  background: "transparent",
  fontSize: 11,
  color: "var(--color-text-info)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};

const card: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: "8px 12px",
  fontSize: 12,
  lineHeight: 1.5,
  color: "var(--color-text-secondary)",
};

const snippetTitle: CSSProperties = {
  color: "var(--color-text-primary)",
  fontWeight: 500,
};
